import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrackedLink } from '@components/analytics/TrackedLink';
import { useTheme } from '@theme/useTheme';
import GradientBackground from '@components/ui/GradientBackground';
import { useAuth } from '@contexts/AuthContext';
import LoginModal from '@components/modals/LoginModal';
import ProfileModal from '@components/modals/ProfileModal';

const About: React.FC = () => {
  const { colors } = useTheme();
  const { isAuthenticated } = useAuth();
  const [showProfile, setShowProfile] = useState(false);

  const handleCloseProfile = () => {
    setShowProfile(false);
  };

  // Global event listeners for modal controls
  useEffect(() => {
    const handleProfileModal = () => setShowProfile(true);

    window.addEventListener('openProfileModal', handleProfileModal);

    return () => {
      window.removeEventListener('openProfileModal', handleProfileModal);
    };
  }, []);

  const features = [
    {
      title: "Comprehensive Assessment",
      description: "Our scientifically-backed questionnaire evaluates multiple risk factors including sleep patterns, medical history, and lifestyle factors.",
      icon: "📊"
    },
    {
      title: "Personalized Results",
      description: "Get detailed insights about your sleep health with personalized recommendations based on your assessment results.",
      icon: "🎯"
    },
    {
      title: "Expert-Backed",
      description: "Developed in collaboration with sleep medicine specialists and based on established clinical guidelines.",
      icon: "👨‍⚕️"
    },
    {
      title: "Privacy-First",
      description: "Your health data is encrypted and secure. We prioritize your privacy and data protection.",
      icon: "🔒"
    }
  ];

  const stats = [
    { number: "22M+", label: "Americans affected by sleep apnea" },
    { number: "80%", label: "Cases remain undiagnosed" },
    { number: "3x", label: "Higher risk of heart disease" },
    { number: "24/7", label: "Assessment availability" }
  ];

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: colors.background }}
    >
      {/* Hero Section */}
      <div className="relative pt-32 pb-8 sm:pt-40 sm:pb-12 lg:pt-48 lg:pb-16 mb-16 sm:mb-20 lg:mb-24 overflow-hidden">
        {/* Gradient background */}
        <GradientBackground position="top" height="medium" />

        <div className="px-6 sm:px-8 lg:px-12 xl:px-16 relative z-10">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
                style={{ color: colors.heading }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              >
                About Sleep Apnea Assessment
              </motion.h1>
              <motion.p
                className="text-xl sm:text-2xl leading-relaxed max-w-4xl mx-auto"
                style={{ color: colors.text }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              >
                Empowering individuals to take control of their sleep health through advanced assessment technology
              </motion.p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">

          {/* What is Sleep Apnea Section */}
          <section className="mt-4 mb-8 sm:mb-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div
                className="p-6 lg:p-8 rounded-tr-2xl rounded-bl-2xl border"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border
                }}
              >
                <h2
                  className="text-3xl sm:text-4xl font-bold mb-6"
                  style={{ color: colors.heading }}
                >
                  What is Sleep Apnea?
                </h2>
                <div className="space-y-4">
                  <p
                    className="text-lg leading-relaxed"
                    style={{ color: colors.text }}
                  >
                    Sleep apnea is a serious sleep disorder where breathing repeatedly stops and starts during sleep.
                    This condition affects millions of people worldwide, often going undiagnosed for years.
                  </p>
                  <p
                    className="text-lg leading-relaxed"
                    style={{ color: colors.text }}
                  >
                    There are three main types of sleep apnea:
                  </p>
                  <ul className="space-y-3 ml-6">
                    <li className="flex items-start">
                      <span className="text-lg mr-3" style={{ color: colors.primary }}>•</span>
                      <span style={{ color: colors.text }}>
                        <strong>Obstructive Sleep Apnea (OSA):</strong> The most common form, caused by blocked airways
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-lg mr-3" style={{ color: colors.primary }}>•</span>
                      <span style={{ color: colors.text }}>
                        <strong>Central Sleep Apnea:</strong> When the brain doesn't send proper signals to breathing muscles
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-lg mr-3" style={{ color: colors.primary }}>•</span>
                      <span style={{ color: colors.text }}>
                        <strong>Complex Sleep Apnea:</strong> A combination of both obstructive and central sleep apnea
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative">
                <div
                  className="absolute inset-0 rounded-tr-[4rem] rounded-bl-[4rem] transform translate-x-4 translate-y-4 -z-10"
                  style={{ backgroundColor: colors.primary }}
                ></div>
                <div
                  className="relative p-8 rounded-tr-[4rem] rounded-bl-[4rem] border"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border
                  }}
                >
                  <h3
                    className="text-2xl font-bold mb-4"
                    style={{ color: colors.heading }}
                  >
                    Common Symptoms
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Loud snoring",
                      "Episodes of stopped breathing",
                      "Gasping for air during sleep",
                      "Morning headache",
                      "Difficulty staying asleep",
                      "Excessive daytime sleepiness"
                    ].map((symptom, index) => (
                      <li key={index} className="flex items-center">
                        <span className="w-2 h-2 rounded-full mr-3" style={{ backgroundColor: colors.primary }}></span>
                        <span style={{ color: colors.text }}>{symptom}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Statistics Section */}
          <section className="mb-8 sm:mb-12">
            <div className="text-center mb-8">
              <h2
                className="text-3xl sm:text-4xl font-bold mb-4"
                style={{ color: colors.heading }}
              >
                The Impact of Sleep Apnea
              </h2>
              <p
                className="text-lg max-w-3xl mx-auto"
                style={{ color: colors.text }}
              >
                Sleep apnea affects millions of people worldwide and can have serious health consequences if left untreated.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center p-6 rounded-tr-2xl rounded-bl-2xl border border-black"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border
                  }}
                >
                  <div
                    className="text-3xl sm:text-4xl font-bold mb-2"
                    style={{ color: colors.primary }}
                  >
                    {stat.number}
                  </div>
                  <div
                    className="text-sm sm:text-base"
                    style={{ color: colors.text }}
                  >
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Features Section */}
          <section className="mb-8 sm:mb-12">
            <div className="text-center mb-8">
              <h2
                className="text-3xl sm:text-4xl font-bold mb-4"
                style={{ color: colors.heading }}
              >
                Why Choose Our Assessment?
              </h2>
              <p
                className="text-lg max-w-3xl mx-auto"
                style={{ color: colors.text }}
              >
                Our comprehensive sleep apnea assessment tool is designed to help you understand your risk factors and take proactive steps toward better sleep health.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="p-6 lg:p-8 rounded-tr-2xl rounded-bl-2xl border"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border
                  }}
                >
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3
                    className="text-xl sm:text-2xl font-bold mb-4"
                    style={{ color: colors.heading }}
                  >
                    {feature.title}
                  </h3>
                  <p
                    className="text-lg leading-relaxed"
                    style={{ color: colors.text }}
                  >
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Call to Action */}
          <section className="text-center mb-8 sm:mb-12">
            <div
              className="p-8 lg:p-12 rounded-tr-2xl rounded-bl-2xl border"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border
              }}
            >
              <h2
                className="text-3xl sm:text-4xl font-bold mb-6"
                style={{ color: colors.heading }}
              >
                Ready to Assess Your Sleep Health?
              </h2>
              <p
                className="text-lg mb-8 max-w-3xl mx-auto"
                style={{ color: colors.text }}
              >
                Take our comprehensive assessment to understand your sleep apnea risk factors and get personalized recommendations for better sleep health.
              </p>
              <TrackedLink
                to="/assessment"
                className="px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 inline-block"
                style={{
                  backgroundColor: colors.primary,
                  color: colors.background
                }}
                gtmId="about_cta_start_assessment"
                gtmSection="page_about"
                gtmLabel="Start Your Assessment"
              >
                Start Your Assessment
              </TrackedLink>
            </div>
          </section>
        </div>
      </main>

      {/* Modals */}
      <LoginModal />

      {isAuthenticated && (
        <ProfileModal
          visible={showProfile}
          onClose={handleCloseProfile}
        />
      )}
    </div>
  );
};

export default About;
